import React, { useEffect } from "react";
import { usePatient } from "../state/patientContext";
import ResultHeader from "./uiControllers/ResultHeader";
import { PatientColorMap } from "../layout/patientColorMap";
import { usePatientRouteChanger } from "../PatientRoutes/routeChanger";
import { useTranslate } from "../hooks/useTranslate";
import { RouteContainer } from "../PatientRoutes/RouteContainer";
import styled from "@mui/material/styles/styled";
import { PatientRoute_Default } from "../PatientRoutes/PatientRoute";
import { useClinicData } from "../clinic/clinicHooks";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useWidgetSettings } from "../widget/useWidgetSettings";
import { ClinicCode } from "./uiControllers/clinicCode";
import { HtmlOrTextContent } from "./uiControllers/HtmlOrTextContent";
import { usePatientFeatureFlags } from "../hooks/usePatientFeatureFlags";
import { FeedbackContainer } from "./feedbackContainer";
import { KahunPatientLinkButton } from "./Questions/kahunPatientLinkButton";
import { ThankYouAnimation } from "./animation/thankYouAnimation";
import { useShowReviewPopup } from "../layout/useShowReviewPopup";
//region [[ Styles ]]
const Container = styled("div")({
  backgroundColor: "white",
});

const RightWaveContainer = styled("div")({
  backgroundColor: PatientColorMap.Header,
});

const Thanks = styled("div")({
  backgroundColor: "white",
  justifyContent: "center",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  borderRadius: "0 80px 0 0",
});

const ThanksTitle = styled("div")({
  fontSize: "28px",
  fontWeight: 600,
  color: PatientColorMap.Primary,
  marginBottom: "10px",
  marginTop: "-35px",
});

const ThanksSubTitle = styled("div")({
  fontSize: "18px",
  color: "black",
  maxWidth: "250px",
  textAlign: "center",
  marginBottom: "15px",
});

const CenteredKahunPatientLinkButton = styled(KahunPatientLinkButton)({
  "& .button-container": {
    justifyContent: "center",
  },
});

const ThanksAnimation = styled(ThankYouAnimation)({
  marginBottom: "10px",
});
//endregion [[ Styles ]]

//region [[ Props ]]
interface ThanksContainerProps extends RouteContainer {}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

//region [[ Texts ]]

//endregion [[ Texts ]]

export const ThanksContainer = ({
  embedded,
  ...props
}: ThanksContainerProps) => {
  const showCodeAtEndOfChat = usePatientFeatureFlags("showCodeAtEndOfChat");
  const allowFeedbackPopupFlag = usePatientFeatureFlags("askForFeedbackPopup");
  const isLocaleChangerFlagActive = usePatientFeatureFlags("localeChanger");

  const { partnerId } = useWidgetSettings();
  const { isClinicMode } = useClinicData();
  const { name: clinicName, clinic: clinicId } = useClinicData();

  // Show review popup if needed
  useShowReviewPopup();

  useEffect(() => {
    evt({
      category: CATEGORY.PATIENT_THANKS,
      action: ACTION.PATIENT_PAGE_LOAD,
      partnerId,
      props: {
        clinicId,
        clinicName,
      },
    });
  }, [partnerId, clinicId, clinicName]);

  const routeChanger = usePatientRouteChanger();

  const { translate } = useTranslate();

  const { shareId, caseId, gender, age, progress, loading } = usePatient();

  const showPatientCardLink = usePatientFeatureFlags("showPatientCardLink");

  if (!progress && !loading && !shareId && !caseId && !embedded) {
    routeChanger(PatientRoute_Default);
    return null;
  }

  return (
    <Container className={props.className}>
      <ResultHeader
        isSummary={false}
        age={age?.toString()}
        gender={gender}
        isLocaleChangerFlagActive={!!isLocaleChangerFlagActive}
      />
      <RightWaveContainer>
        <Thanks>
          <ThanksAnimation size={300} />
          <ThanksTitle>{translate("THANKS_TITLE")}</ThanksTitle>
          {translate("THANK_YOU_PATIENT") !== "" &&
            translate("THANK_YOU_PATIENT") && (
              <ThanksSubTitle>
                <HtmlOrTextContent content={translate("THANK_YOU_PATIENT")} />
              </ThanksSubTitle>
            )}
          {!isClinicMode && (
            <ThanksSubTitle>
              <HtmlOrTextContent content={translate("THANKS_SUB_TITLE")} />
            </ThanksSubTitle>
          )}
          {showPatientCardLink && <CenteredKahunPatientLinkButton />}
          {(isClinicMode || showCodeAtEndOfChat) && <ClinicCode />}
          {!allowFeedbackPopupFlag && <FeedbackContainer />}
        </Thanks>
      </RightWaveContainer>
    </Container>
  );
};
